import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { FormikProvider, useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import OtpInput from 'react-otp-input';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { datadogLogs } from '@datadog/browser-logs'

import { backendRoutes, frontendRoutes } from '../../../Constants/constants';
import { setOtpProofToken } from '../../../redux/action/action';
import { Button } from '../../Button/Button';
import Loader from '../../Loader/Loader';
import { BuilderSettings } from '../../../redux/types/builders';
import { SurveyNavigationState } from '../../../types/navigation';
import { requiresPersonaVerification } from '../../../utils/requiresPersonaVerification';

const OtpVerification = () => {
  const { t: translate } = useTranslation();
	const history = useHistory();
	const dispatch = useDispatch();
	const navigationState = history.location.state as SurveyNavigationState | undefined;
	const previousPath = () => {
		history.goBack();
	}
	let params = useParams<{ builder_name?: string }>();
	const phoneNumber = useSelector((state: any) => state.phNumber.phNumber?.phone)
	const [showAlert, setShowAlert] = useState(false);
	const [message, setMessage] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [retryAfter, setRetryAfter] = useState(0);

  	const builderSettings: BuilderSettings = useSelector((state: any) => state.builder.settings);

	useEffect(() => {
		if (retryAfter <= 0) return;
		const timer = setTimeout(() => setRetryAfter(s => s - 1), 1000);
		return () => clearTimeout(timer);
	}, [retryAfter]);

	const resend_otp = () => {
		axios({
			baseURL: backendRoutes.MOBILE_BASE_URL,
			url: backendRoutes.get_otp,
			method: "post",
			data: {
				phone: phoneNumber
			}
		}).then((res) => {
			localStorage.setItem("secure_token", res.data.secret);
			setShowAlert(true);
			setMessage(translate('otp_sent'));
			setTimeout(() => setShowAlert(false), 3000);
		}).catch((err) => {
			if (err?.response?.status === 429) {
				const seconds = err.response?.data?.retryAfter ?? 60;
				setRetryAfter(seconds);
			} else {
				setShowAlert(true);
				setMessage(translate('otp_resend_error'));
				setTimeout(() => setShowAlert(false), 3000);
			}
		})
	}

	const otp_verify = (formData: any) => {
		setIsLoading(true);
		axios({
			baseURL: backendRoutes.MOBILE_BASE_URL,
			url: backendRoutes.otp_verify,
			method: "POST",
			data: formData
		}).then((res) => {
			const token = res.data?.otp_proof_token;
			if (token) {
				setIsLoading(false);
				datadogLogs.logger.info(`Phone number ${phoneNumber} confirmed`)
				localStorage.removeItem('phone_numbers_verification');
				dispatch(setOtpProofToken(token));

				!requiresPersonaVerification(builderSettings)
					? history.push(frontendRoutes.isThisYou(params.builder_name).path, navigationState)
					: history.push(frontendRoutes.textVerify(params.builder_name).path, navigationState);
			} else {
				setIsLoading(false);
				datadogLogs.logger.warn(`Phone number ${phoneNumber} is not confirmed`)
				setShowAlert(true);
				setMessage(translate('otp_not_verified'));
				setTimeout(() => setShowAlert(false), 3000);
			}
		}).catch((err) => {
			datadogLogs.logger.warn(`Phone number ${phoneNumber} is not confirmed`)
			setIsLoading(false);

			if (err?.response?.status === 400) {
				setShowAlert(true);
				setMessage(translate('otp_invalid'));
			} else if (err?.response?.status === 403) {
				const msg = err.response.data;
				setShowAlert(true);
				setMessage(msg);
			} else {
				setShowAlert(true);
				setMessage(translate('generic_retry'));
			}
			setTimeout(() => setShowAlert(false), 3000);
		})
	}

	const getResendOtp = (e: any) => {
		e.preventDefault();
		resend_otp()
	}

	const formik = useFormik({
		initialValues: {
			otp: ''
		},
		onSubmit: (values) => {
			const data = {
				phone: phoneNumber,
				code: values.otp,
				secret: localStorage.getItem('secure_token'),
			}
			otp_verify(data)
		}
	})

	return (
		<section className="main-section otp-screen d-flex justify-content-center">
			<div className="box">
				<div className="main-title">
					<h1>{translate('phone_verification')}</h1>
					<span><sup>*</sup>{translate('required')}</span>
				</div>
				<span onClick={previousPath} className="link-back icn icn-back-arrow"></span>
				<div className="box-data d-flex flex-column">
					<Form onSubmit={formik.handleSubmit} className="h-100 d-flex flex-column">
						<FormikProvider value={formik}>
							<Form.Group>
								<Form.Label>{translate('enter_otp')}<sup>*</sup></Form.Label>
								<div className="input-box d-flex justify-content-between flex-column otp-code">
									<OtpInput
										value={formik.values.otp}
										onChange={(ot: any) => formik.setFieldValue('otp', ot)}
										numInputs={6}
										separator={<span> </span>}
										isInputNum={true}
										isInputSecure={false}
										inputStyle="form-control"
										containerStyle="w-100 justify-content-between"
										placeholder="000000"
									/>
									<span className="invalid-feedback d-block">
										{showAlert ? message : ""}
									</span>
								</div>
							</Form.Group>
						</FormikProvider>
						<span className="resend-link">
							{translate('having_trouble')}{' '}
							{retryAfter > 0 ? (
								<span>{translate('retry_in', { seconds: retryAfter })}</span>
							) : (
								<span className="a" onClick={getResendOtp}>{translate('resend')}</span>
							)}
						</span>
						{isLoading && <Loader />}
						<div className="box-footer mt-auto">
              <Button type="submit" className="button w-100">
                {translate('next')}
              </Button>
						</div>
					</Form>

				</div>
			</div>
		</section>
	)
}

export default OtpVerification;
